import {
  autoscaleConditionTypes,
  autoscaleConditionOperators,
} from '../CreateAutoscaleRulesModal/autoscaleRulesMenuItems';

import type {
  Condition,
  ConditionOperator,
} from '../CreateAutoscaleRulesModal/useAutoscaleRulesBuilder';

type JsonLogicRule = Record<string, unknown>;

function rid() {
  return Math.random().toString(36).slice(2, 8);
}

/**
 * Parse JSON Logic rule back to UI condition format
 */
export function parseJsonLogicToConditions(jsonLogic: JsonLogicRule): Condition[] {
  const conditions: Condition[] = [];

  // Check if it's a top-level 'and' with multiple conditions
  if (jsonLogic.and && Array.isArray(jsonLogic.and)) {
    const andConditions = jsonLogic.and as JsonLogicRule[];

    // Group conditions by variable type
    const groupedByType: Record<string, JsonLogicRule[]> = {};

    for (const condition of andConditions) {
      const varName = extractVarName(condition);
      if (varName) {
        if (!groupedByType[varName]) {
          groupedByType[varName] = [];
        }
        groupedByType[varName].push(condition);
      }
    }

    // Convert each group to a UI condition
    for (const [varName, rules] of Object.entries(groupedByType)) {
      const uiCondition = parseConditionGroup(varName, rules);
      if (uiCondition) {
        conditions.push(uiCondition);
      }
    }
  } else {
    // Single condition (no 'and')
    const varName = extractVarName(jsonLogic);
    if (varName) {
      const uiCondition = parseConditionGroup(varName, [jsonLogic]);
      if (uiCondition) {
        conditions.push(uiCondition);
      }
    }
  }

  return conditions.length > 0 ? conditions : getDefaultCondition();
}

function extractVarName(rule: JsonLogicRule): string | null {
  // Handle 'some' and 'none'
  if (rule.some && Array.isArray(rule.some)) {
    const first = rule.some[0];
    // Tags case: first element is { var: "tags" }
    if (first && typeof first === 'object' && 'var' in first) {
      return (first as { var: string }).var;
    }
    // Single field contains case: check second element
    const second = rule.some[1];
    if (second && typeof second === 'object' && 'in' in second) {
      const inArray = (second as { in: unknown[] }).in;
      if (Array.isArray(inArray) && inArray.length >= 2) {
        const varObj = inArray[1];
        if (varObj && typeof varObj === 'object' && 'var' in varObj) {
          return (varObj as { var: string }).var;
        }
      }
    }
  }
  if (rule.none && Array.isArray(rule.none)) {
    const first = rule.none[0];
    // Tags case: first element is { var: "tags" }
    if (first && typeof first === 'object' && 'var' in first) {
      return (first as { var: string }).var;
    }
    // Single field doesn't contain case: check second element
    const second = rule.none[1];
    if (second && typeof second === 'object' && 'in' in second) {
      const inArray = (second as { in: unknown[] }).in;
      if (Array.isArray(inArray) && inArray.length >= 2) {
        const varObj = inArray[1];
        if (varObj && typeof varObj === 'object' && 'var' in varObj) {
          return (varObj as { var: string }).var;
        }
      }
    }
  }

  // Handle 'in' directly (exact match)
  if (rule.in && Array.isArray(rule.in)) {
    const varObj = rule.in[0] as { var?: string };
    return varObj?.var || null;
  }

  // Handle '!' (negation)
  if (rule['!']) {
    const negated = rule['!'] as JsonLogicRule;
    if (negated.in && Array.isArray(negated.in)) {
      const varObj = negated.in[0] as { var?: string };
      return varObj?.var || null;
    }
  }

  return null;
}

function parseConditionGroup(varName: string, rules: JsonLogicRule[]): Condition | null {
  // Map varName to UI type
  const typeMapping: Record<string, string> = {
    tags: 'volume',
    instance_tags: 'instance_tag',
    instance_id: 'instance',
    node_group_name: 'node',
    cluster_name: 'cluster',
  };

  const tag = typeMapping[varName];
  if (!tag) {
    return null;
  }

  const type = autoscaleConditionTypes.find((t) => t.tag === tag) || null;
  if (!type) {
    return null;
  }

  const conditionOperators = rules
    .map((rule) => parseConditionOperator(rule, tag))
    .filter((op): op is NonNullable<typeof op> => op !== null);

  if (conditionOperators.length === 0) {
    return null;
  }

  return {
    id: rid(),
    type,
    conditionOperators,
  };
}

function parseConditionOperator(rule: JsonLogicRule, tag: string): ConditionOperator | null {
  // Volume tags and Instance tags: 'some' or 'none' array iteration
  if (tag === 'volume' || tag === 'instance_tag') {
    // Exact match: { some: [{ var: "tags" }, { in: [{ var: "" }, [values]] }] }
    if (rule.some && Array.isArray(rule.some)) {
      const inClause = rule.some[1] as JsonLogicRule;

      // Check if it's exact match or contains
      if (inClause.in && Array.isArray(inClause.in)) {
        const values = inClause.in[1] as string[];
        const condition = autoscaleConditionOperators.find((op) => op.operator === 'is');
        return {
          id: rid(),
          condition: condition || null,
          value: Array.isArray(values) ? values : [],
        };
      }
      // Contains match: { some: [{ var: "tags" }, { or: [{ in: [val, { var: "" }] }] }] }
      else if (inClause.or && Array.isArray(inClause.or)) {
        const values: string[] = [];
        inClause.or.forEach((item: unknown) => {
          if (item && typeof item === 'object' && 'in' in item) {
            const inArray = (item as { in: unknown[] }).in;
            if (Array.isArray(inArray) && inArray.length >= 1) {
              const val = inArray[0];
              if (typeof val === 'string') {
                values.push(val);
              }
            }
          }
        });
        const condition = autoscaleConditionOperators.find((op) => op.operator === 'contains');
        return {
          id: rid(),
          condition: condition || null,
          value: values,
        };
      }
    }

    // Exact exclude: { none: [{ var: "tags" }, { in: [{ var: "" }, [values]] }] }
    if (rule.none && Array.isArray(rule.none)) {
      const inClause = rule.none[1] as JsonLogicRule;

      // Check if it's exact exclude or doesn't contain
      if (inClause.in && Array.isArray(inClause.in)) {
        const values = inClause.in[1] as string[];
        const condition = autoscaleConditionOperators.find((op) => op.operator === 'is not');
        return {
          id: rid(),
          condition: condition || null,
          value: Array.isArray(values) ? values : [],
        };
      }
      // Doesn't contain: { none: [{ var: "tags" }, { or: [{ in: [val, { var: "" }] }] }] }
      else if (inClause.or && Array.isArray(inClause.or)) {
        const values: string[] = [];
        inClause.or.forEach((item: unknown) => {
          if (item && typeof item === 'object' && 'in' in item) {
            const inArray = (item as { in: unknown[] }).in;
            if (Array.isArray(inArray) && inArray.length >= 1) {
              const val = inArray[0];
              if (typeof val === 'string') {
                values.push(val);
              }
            }
          }
        });
        const condition = autoscaleConditionOperators.find(
          (op) => op.operator === "doesn't contain"
        );
        return {
          id: rid(),
          condition: condition || null,
          value: values,
        };
      }
    }
  }

  // Single value fields
  // Exact match: { in: [{ var: "field" }, [values]] }
  if (rule.in && Array.isArray(rule.in)) {
    const values = rule.in[1] as string[];
    const condition = autoscaleConditionOperators.find((op) => op.operator === 'is');
    return {
      id: rid(),
      condition: condition || null,
      value: Array.isArray(values) ? values : [],
    };
  }

  // Exact exclude: { !: { in: [{ var: "field" }, [values]] } }
  if (rule['!']) {
    const negated = rule['!'] as JsonLogicRule;
    if (negated.in && Array.isArray(negated.in)) {
      const values = negated.in[1] as string[];
      const condition = autoscaleConditionOperators.find((op) => op.operator === 'is not');
      return {
        id: rid(),
        condition: condition || null,
        value: Array.isArray(values) ? values : [],
      };
    }
  }

  // Contains match: { some: [[values], { in: [{ var: "" }, { var: "field" }] }] }
  if (rule.some && Array.isArray(rule.some)) {
    const first = rule.some[0];
    if (Array.isArray(first)) {
      const condition = autoscaleConditionOperators.find((op) => op.operator === 'contains');
      return {
        id: rid(),
        condition: condition || null,
        value: first.filter((v) => typeof v === 'string') as string[],
      };
    }
  }

  // Doesn't contain: { none: [[values], { in: [{ var: "" }, { var: "field" }] }] }
  if (rule.none && Array.isArray(rule.none)) {
    const first = rule.none[0];
    if (Array.isArray(first)) {
      const condition = autoscaleConditionOperators.find((op) => op.operator === "doesn't contain");
      return {
        id: rid(),
        condition: condition || null,
        value: first.filter((v) => typeof v === 'string') as string[],
      };
    }
  }

  return null;
}

function getDefaultCondition(): Condition[] {
  return [
    { id: rid(), type: null, conditionOperators: [{ id: rid(), condition: null, value: [] }] },
  ];
}
